import React from 'react';
import Image from 'next/legacy/image';
import { Block, BLOCKS, Inline, INLINES } from '@contentful/rich-text-types';

import { ISplitFormFields } from '@/types/contentful';

import { Flexbox } from '../../Flexbox';
import { Text } from '../../Text';
import RichText from '../../RichText';
import { EmbeddedInlineEntryNode } from '../../RichText/Nodes';

import { InlineAnchor } from '../../RichText/styles';
import { SpinningBadge, WhiteCheckmark } from './styles';
import { getImageData } from '../../../utils/contentful';

import tealBadge from '@/images/teal-badge.svg';
import checkmark from '@/images/check.svg';
import GlImage from '~/components/Image';
import { SocialShare } from '~/components/LearningCenter/Components/ArticleHeader/SocialShare';

interface SuccessProps
  extends Pick<
    ISplitFormFields,
    'successHeading' | 'successMessage' | 'successImage' | 'successSocialShare'
  > {
  customSuccessMessage?: string;
}

export function FormSuccess({
  successHeading,
  successMessage,
  successImage,
  customSuccessMessage,
  successSocialShare
}: SuccessProps) {
  return (
    <Flexbox
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      padding={{ 0: ['2xl', 'ml'], desktop: '2xl' }}
    >
      <div className="relative mb-4 flex h-[88px] w-[88px] items-center justify-center">
        {successImage != null ? (
          <GlImage
            {...getImageData(successImage)}
            heroImage={false}
            layout="fill"
            objectFit="fill"
            alt="Success custom image for submitting form"
          />
        ) : (
          <>
            <SpinningBadge>
              <Image
                src={tealBadge}
                alt="Rotating green badge"
                height={88}
                width={88}
              />
            </SpinningBadge>
            <WhiteCheckmark>
              <Image
                src={checkmark}
                alt="Success checkmark for submitting form"
                height={30}
                width={36}
              />
            </WhiteCheckmark>
          </>
        )}
      </div>

      <Text
        as="h4"
        textAlign="center"
        fontWeight="500"
        fontSize={{ desktop: 'l' }}
        lineHeight={{ desktop: 'm' }}
        marginBottom="s"
      >
        {successHeading}
      </Text>

      {successMessage && !customSuccessMessage && (
        <RichText
          data={successMessage}
          overrideOptions={{
            [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => (
              <Text
                as="p"
                textAlign="center"
                fontWeight="400"
                fontSize={{ desktop: 'm' }}
                lineHeight={{ desktop: 'm' }}
                colour="defaultText"
              >
                {children}
              </Text>
            ),
            [INLINES.HYPERLINK]: (node, children) => (
              <InlineAnchor href={node.data.uri} colour="defaultText">
                {children}
              </InlineAnchor>
            ),
            [INLINES.EMBEDDED_ENTRY]: (node, children) => (
              <EmbeddedInlineEntryNode
                node={node}
                colour="defaultText"
                fontWeight="p"
              >
                {children}
              </EmbeddedInlineEntryNode>
            )
          }}
        />
      )}

      {customSuccessMessage && (
        <Text
          as="p"
          textAlign="center"
          fontWeight="400"
          fontSize={{ desktop: 'm' }}
          lineHeight={{ desktop: 'm' }}
          colour="defaultText"
        >
          {customSuccessMessage}
        </Text>
      )}
      <div className="mt-[10px] w-full items-center justify-center [&>div]:!justify-center">
        <SocialShare inline hideShareCopy socialOptions={successSocialShare} />
      </div>
    </Flexbox>
  );
}
